import React from "react";
import { router } from "@inertiajs/react";
import { apiPost, apiDelete } from "../api";
import { buildLoginUrl } from "./loginRedirectUrl";

// Wraps registration + cancellation mutations for the rebranded webinar
// detail page. On success, asks Inertia to partial-reload just the
// `webinar` prop so the server-computed `cta` (and everything else the
// presenter derives) becomes the authoritative post-mutation state. This
// avoids reimplementing subtle server logic on the client.
//
// Optimistic updates only flip `is_registered` + `remaining_quota` so the
// UI feels snappy. If the request fails we roll those back and surface
// the service's error message through `error`.
//
// Session expiry (401/302) is treated as "log in first" and redirects to
// the login URL; the user returns to the same webinar page.
export default function useWebinarRegistration({ webinar, setWebinar, authRebrand }) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const refreshFromServer = React.useCallback(() => {
    router.reload({ only: ["webinar"] });
  }, []);

  const register = React.useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);

    const prev = webinar;
    const optimistic = applyOptimisticRegister(webinar);
    setWebinar(optimistic);

    try {
      const { parsedResponse, statusCode } = await apiPost("/user_webinars_v2", {
        body: { user_webinar: { webinar_id: webinar.id } },
      });

      if (statusCode === 401 || statusCode === 302) {
        window.location.href = buildLoginUrl(webinar.slug, authRebrand);
        return;
      }

      // create_v2 returns status: "failed" inside a 200 JSON envelope when
      // the service rejects (quota full, not subscribed, wrong company, …).
      const succeeded =
        statusCode >= 200 &&
        statusCode < 300 &&
        parsedResponse &&
        parsedResponse.status &&
        parsedResponse.status !== "failed";

      if (succeeded) {
        refreshFromServer();
      } else {
        setWebinar(prev);
        setError(
          (parsedResponse && parsedResponse.error_message) ||
            "Pendaftaran gagal. Silakan coba lagi.",
        );
      }
    } catch {
      setWebinar(prev);
      setError("Tidak dapat terhubung ke server.");
    } finally {
      setBusy(false);
    }
  }, [busy, webinar, setWebinar, authRebrand, refreshFromServer]);

  const cancel = React.useCallback(async () => {
    if (busy) return;
    if (!webinar.user_webinar_id) return;
    setBusy(true);
    setError(null);

    const prev = webinar;
    const optimistic = applyOptimisticCancel(webinar);
    setWebinar(optimistic);

    try {
      const { parsedResponse, statusCode } = await apiDelete(
        `/user_webinars/${webinar.user_webinar_id}`,
      );

      if (statusCode === 401 || statusCode === 302) {
        window.location.href = buildLoginUrl(webinar.slug, authRebrand);
        return;
      }

      if (statusCode >= 200 && statusCode < 300) {
        refreshFromServer();
      } else {
        setWebinar(prev);
        setError(
          (parsedResponse && parsedResponse.error_message) ||
            "Pembatalan gagal. Silakan coba lagi.",
        );
      }
    } catch {
      setWebinar(prev);
      setError("Tidak dapat terhubung ke server.");
    } finally {
      setBusy(false);
    }
  }, [busy, webinar, setWebinar, authRebrand, refreshFromServer]);

  const dismissError = React.useCallback(() => setError(null), []);

  return { register, cancel, busy, error, dismissError };
}

function applyOptimisticRegister(w) {
  const remaining = Math.max(0, (w.remaining_quota || 0) - 1);
  return {
    ...w,
    is_registered: true,
    remaining_quota: remaining,
    capacity_label: formatCapacityLabel(remaining, w.unlimited_capacity),
    cta: {
      ...(w.cta || {}),
      kind: "registered",
      label: "Terdaftar",
      variant: "secondary",
      disabled: true,
    },
  };
}

function applyOptimisticCancel(w) {
  const remaining = (w.remaining_quota || 0) + 1;
  return {
    ...w,
    is_registered: false,
    user_webinar_id: null,
    can_cancel: false,
    remaining_quota: remaining,
    capacity_label: formatCapacityLabel(remaining, w.unlimited_capacity),
    cta: {
      ...(w.cta || {}),
      kind: "register",
      label: "Daftar Sekarang",
      variant: "primary",
      disabled: false,
    },
  };
}

function formatCapacityLabel(remaining, unlimited) {
  if (unlimited) return "Tanpa Batas";
  return `Tersisa ${Math.max(0, remaining)}`;
}
